/**
 * The claim lifecycle, as one list both sides read.
 *
 * `promo.promotion_claims.status` is a plain varchar with no enum behind it, and
 * every screen that renders a claim used to carry its own hand-typed copy of the
 * values. Three copies existed and all three had drifted: the player's list was
 * missing `revoked` so an admin clawback rendered in neither the active list nor
 * the history one, the backoffice filter could not select `revoked` at all, and
 * `PromotionClaimView` below offered `cleared`, `released_by_admin` and
 * `expired`, none of which svc-promo has ever written.
 *
 * This file has no typebox import on purpose — both SPAs consume it, and the
 * status list should not drag a schema library into a browser bundle.
 *
 * `promo-claim-status.test.ts` reads the svc-promo sources and fails when a write
 * site names a status this list does not, which is the part that keeps the two
 * sides agreeing without anyone remembering to.
 */

/**
 * Every value svc-promo can leave in `promo.promotion_claims.status`.
 *
 * `cancelled` is retained rather than written: no code path sets it, but the
 * payout sum, the budget rollup and the prior-claim gates all still name it, so
 * a legacy row carrying it has to stay renderable.
 */
export const PROMOTION_CLAIM_STATUSES = [
  'pending',
  'approved',
  'active',
  'completed',
  'released',
  'rejected',
  'forfeited',
  'revoked',
  'cancelled',
] as const;

export type PromotionClaimStatus = (typeof PROMOTION_CLAIM_STATUSES)[number];

/**
 * What the status says about the granted amount sitting on the row.
 *
 * - `credited` — the principal reached the player's pool, locked or free.
 * - `awaiting` — nothing is promised yet. `pending` cannot say more than that on
 *   its own: the approval queue inserts it with no money moved, and a bulk drop
 *   inserts it having credited the wallet in the same transaction.
 * - `withheld` — the claim ended without the player keeping the grant.
 *
 * Any surface that renders the amount must branch on this and not on the
 * presence of an amount. `granted_amount` survives a forfeit, a rejection and an
 * admin clawback completely unchanged, so a green `+ ฿50` drawn from the column
 * alone tells a robbed player they still have the money.
 */
export const PROMOTION_CLAIM_OUTCOME: Record<
  PromotionClaimStatus,
  'awaiting' | 'credited' | 'withheld'
> = {
  pending: 'awaiting',
  approved: 'credited',
  active: 'credited',
  completed: 'credited',
  released: 'credited',
  rejected: 'withheld',
  forfeited: 'withheld',
  revoked: 'withheld',
  cancelled: 'withheld',
};

const KNOWN_STATUSES: ReadonlySet<string> = new Set(PROMOTION_CLAIM_STATUSES);

/** Whether `status` is one this build knows. Membership, not an index lookup:
 *  the value arrives off the wire, and `'constructor'` indexes an object
 *  literal to something truthy. */
export function isPromotionClaimStatus(
  status: string | null | undefined,
): status is PromotionClaimStatus {
  return status != null && KNOWN_STATUSES.has(status);
}

/**
 * Outcome of an arbitrary status string, for the many call sites that hold the
 * value as a bare `string` off the wire.
 *
 * An unrecognised status resolves to `awaiting`, never `credited`: a build that
 * has not heard of a status must not announce money on its behalf.
 */
export function promotionClaimOutcome(
  status: string | null | undefined,
): 'awaiting' | 'credited' | 'withheld' {
  return isPromotionClaimStatus(status) ? PROMOTION_CLAIM_OUTCOME[status] : 'awaiting';
}
